/**
 * Per-plan pages (/plan-1 ... /postgraduate-loan) and plan-vs-plan pages.
 * Every figure comes from ratesData.plans, so the April/September update
 * of figures.js flows through here untouched. Copy that mentions a number
 * builds it from the plan object; planPages.test.js greps this file and the
 * page components for £ literals.
 */
import ratesData from "../data/ratesData";

export const EXAMPLE_SALARIES = [30000, 45000, 60000];

export const planById = (id) => ratesData.plans.find((p) => p.id === id);

export const gbp = (n) => `£${Math.round(Number(n)).toLocaleString("en-GB")}`;

/** Yearly repayment at a salary: rate% of everything above the threshold. */
export const yearlyRepayment = (plan, salary) =>
  Math.max(0, salary - plan.threshold) * (plan.repaymentRate / 100);

export const monthlyRepayment = (plan, salary) =>
  yearlyRepayment(plan, salary) / 12;

/** Slug for a plan's own page. */
export const planPath = (id) =>
  id === "postgrad" ? "/postgraduate-loan" : `/${id.replace("plan", "plan-")}`;

/** The main calculator with this plan preselected. */
export const calculatorPath = (id) =>
  `/?plan=${id === "postgrad" ? "planPostgrad" : id}`;

const extraFaq = {
  plan1: (p) => ({
    q: "Is Plan 1 the same in Northern Ireland?",
    a: `Yes. Northern Irish loans use the same ${gbp(p.threshold)} threshold, ${p.repaymentRate}% rate and interest rule as English and Welsh Plan 1 loans.`,
  }),
  plan2: (p) => ({
    q: "Why does my balance keep growing?",
    a: `Your balance grows when interest added exceeds your repayments. Plan 2 interest is ${p.interestRate}. Required repayments follow your income, not the balance, and anything left is written off ${p.writeOff}.`,
  }),
  plan4: (p) => ({
    q: "Is Plan 4 the same as Plan 1?",
    a: `Same interest rule and ${p.repaymentRate}% rate, but a higher threshold of ${gbp(p.threshold)} and a write-off ${p.writeOff}.`,
  }),
  plan5: (p) => ({
    q: `Why is the write-off ${p.writeOffYears} years?`,
    a: `Government rules set Plan 5's write-off at ${p.writeOffYears} years. It has a threshold of ${gbp(p.threshold)} and interest at ${p.interestRate}. Whether you repay for that long depends on your balance, earnings and future rates.`,
  }),
  postgrad: (p) => ({
    q: "I also have an undergraduate loan. Do both come off my pay?",
    a: `Yes. The Postgraduate Loan takes ${p.repaymentRate}% of pay above ${gbp(p.threshold)} at the same time as your undergraduate plan takes its share. The two are separate loans with separate write-off dates.`,
  }),
};

/** Four FAQs for a plan page, built from the plan's own figures. */
export const planFaqs = (plan) => {
  const rate = `${plan.repaymentRate}%`;
  const threshold = gbp(plan.threshold);
  const base = [
    {
      q: "Do I repay while studying?",
      a: "Usually repayments start the April after leaving. For part-time courses longer than four years that started before January 2027, repayment can start in the April four years after the course began. Income must exceed the applicable threshold.",
    },
    {
      q: `What if I earn under ${threshold}?`,
      a: "Deductions stop when pay is below the threshold for that pay period. A bonus can trigger a deduction even if your annual income is below the yearly threshold; you can request a refund after the tax year ends.",
    },
    {
      q: "Does the interest rate change my monthly repayment?",
      a: `Required repayments use ${rate} of earnings above the threshold for your pay period. The annual threshold is ${threshold}. Interest affects the balance and how long you repay, rather than this income-based rate.`,
    },
  ];
  const extra = extraFaq[plan.id];
  if (extra) {
    const { q, a } = extra(plan);
    base.push({ q, a });
  }
  return base;
};

export const planStartWindow = (id) => planById(id)?.who || "";

/** Plan-vs-plan pages. `question` is the H1; `answer` is built at render time. */
export const COMPARISONS = [
  {
    slug: "plan-2-vs-plan-5",
    a: "plan2",
    b: "plan5",
    question: "Plan 2 vs Plan 5",
    context:
      "Plan 5 replaced Plan 2 for English students from August 2023. Welsh students stayed on Plan 2.",
  },
  {
    slug: "plan-1-vs-plan-2",
    a: "plan1",
    b: "plan2",
    question: "Plan 1 vs Plan 2",
    context:
      "Plan 2 replaced Plan 1 for English and Welsh students from September 2012.",
  },
  {
    slug: "plan-2-vs-plan-4",
    a: "plan2",
    b: "plan4",
    question: "Plan 2 vs Plan 4",
    context:
      "Plan 4 covers loans funded by Scotland. Plan 2 covers qualifying loans funded by England from 2012 to July 2023, and Wales from September 2012 onwards.",
  },
  {
    slug: "plan-4-vs-plan-5",
    a: "plan4",
    b: "plan5",
    question: "Plan 4 vs Plan 5",
    context:
      "A Scottish student and an English student starting the same year are on different plans with different thresholds.",
  },
  {
    slug: "undergraduate-vs-postgraduate-loan",
    a: "plan2",
    b: "postgrad",
    question: "Undergraduate vs Postgraduate Loan",
    context:
      "Someone with both loans repays both at once out of the same pay. This compares a Plan 2 undergraduate loan with the Postgraduate Loan.",
  },
];

export const comparisonBySlug = (slug) =>
  COMPARISONS.find((c) => c.slug === slug);

/** Which plan takes less each month at a salary, or null if equal. */
export const cheaperAt = (planA, planB, salary) => {
  const a = monthlyRepayment(planA, salary);
  const b = monthlyRepayment(planB, salary);
  if (Math.abs(a - b) < 0.5) return null;
  return a < b ? planA : planB;
};
